import { FolderLocation } from 'models/types';

const QueryKeys = {
    APP: (path: string) => ['app', path],
    APPLICATIONS_ACCESS_LIST: ['applications-access-list'],
    ASSESSMENTS: (limit?: number, offset?: number, searchTerm?: string) => ['assessments', limit, offset, searchTerm],
    ASSESSMENT_CONSENT: ['assessment-consent'],
    AUDIENCE_COUNT: (audienceId: string, channel?: string) => ['audience-count', audienceId, channel],
    CAMPAIGN_AUDIENCE: ['campaign-audience'],
    CAMPAIGNS_BASE: ['campaigns'],
    CAMPAIGNS: (searchTerm?: string, statuses?: string) => ['campaigns', searchTerm, statuses],
    CAMPAIGNS_PAGINATED: (
        searchTerm?: string,
        statuses?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['campaigns', 'paginated', searchTerm, statuses, offset, limit, sortColumn, sortDirection],
    COMMUNICATION_TYPES_LIST: ['communication-types-list'],
    CONTACTS: (segmentId?: number, searchTerm?: string) => ['contacts', segmentId, searchTerm],
    CONTACTS_DOWNLOAD: (segmentId: number) => ['contacts-download', segmentId],
    CONTACTS_PAGINATED: (
        segmentId?: number,
        searchTerm?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['contacts-paginated', segmentId, searchTerm, offset, limit, sortColumn, sortDirection],
    CONTACT_COLUMN_TOP_VALUES: (columnName: string) => ['contactColumnTopValues', columnName],
    CONTACT_COLUMNS_METADATA: ['contact-columns-metadata'],
    CONTACT_COLUMNS_METADATA_EXTENDED: (recordTypes?: string[]) => ['contact-columns-metadata-extended', recordTypes],
    CONTACT_EVENT_COLUMNS_METADATA: ['contact-event-columns-metadata'],
    CONTACT_EVENT_COLUMNS_METADATA_EXTENDED: (recordTypes?: string[]) => [
        'contact-event-columns-metadata-extended',
        recordTypes,
    ],
    CONTENT: (folderId?: number, type?: string, searchTerm?: string, status?: string) => [
        'content',
        folderId,
        type,
        searchTerm,
        status,
    ],
    CONTENT_PAGINATED: (
        folderId?: number,
        type?: string,
        searchTerm?: string,
        status?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['content-paginated', folderId, type, searchTerm, status, offset, limit, sortColumn, sortDirection],
    CAMPAIGN_AUDIT: (campaignId: string) => ['campaign-audit', campaignId],
    CAMPAIGN_EXIT_STATS: (campaignId: string) => ['campaign-exit-stats', campaignId],
    CURATION_ID: (curationId: string) => ['curation-id', curationId],
    DIRECT_MAIL_ID: (directMailId: number) => ['direct-mail-id', directMailId],
    DIRECT_MAIL_SENDER_PROFILE_ID: (directMailSenderProfileId: number) => [
        'direct-mail-sender-profile',
        directMailSenderProfileId,
    ],
    DIRECT_MAILS: ['direct-mails'],
    FILE_UPLOAD: ['file-upload'],
    FEATURE_SETTINGS: ['feature-settings'],
    FEATURE_SETTINGS_BY_NAME: (featureName: string) => [`feature-settings/${featureName}`],
    FOLDER_LINEAGE: (folderId: number, folderLocation: FolderLocation) => [`${folderLocation}-breadcrumb/${folderId}`],
    IMPORTS: ['imports'],
    LANDING_PAGES: ['landing-pages'],
    LANDING_PAGE_ID: (landingPageId: string) => ['landing-page-id', landingPageId],
    LANDING_PAGE_HTML: (landingPageId: number) => ['landing-page-html', landingPageId],
    PREVIEW_AUDIENCE: ['preview-audience'],
    PREVIEW_EMAIL: (contactCuredId: string, templateId: number, senderProfileId: number) => [
        'preview-email',
        contactCuredId,
        `${templateId}`,
        `${senderProfileId}`,
    ],
    RESPONSE_BY_KEYWORD: (campaignId: string) => [`sms-dashboard/response-by-keyword/${campaignId}`],
    SAVED_ROWS_CATEGORY: (categoryHandle: string) => ['saved-rows', 'category', categoryHandle],
    /** Distinct library category handles (GET /saved-rows/categories). */
    SAVED_ROWS_CATEGORY_LIST: ['saved-rows', 'category-list'],
    /** GET /saved-rows/template-links/:templateId — Bee row uuid → saved_row for this template. */
    SAVED_ROWS_TEMPLATE_LINKS: (templateId: number) => ['saved-rows', 'template-links', `${templateId}`],
    SEGMENTS: ['segments'],
    SEGMENT_COHORTS: (limit?: number, offset?: number, searchTerm?: string) => [
        'segment-cohorts',
        limit,
        offset,
        searchTerm,
    ],
    SEGMENT_COHORT_ID: (cohortId: string) => ['cohort-id', cohortId],
    SEGMENT_FILTERS: ['segment-filters'],
    SEGMENT_ID: (segmentId: number) => ['segment-id', segmentId],
    SEGMENT_FOLDERS: (folderId?: number, type?: string, searchTerm?: string) => [
        'segments',
        folderId,
        type,
        searchTerm,
    ],
    SEGMENTS_PAGINATED: (
        folderId?: number,
        type?: string,
        searchTerm?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['segments', 'paginated', folderId, type, searchTerm, offset, limit, sortColumn, sortDirection],
    SENDER_PROFILE_ID: (senderProfileId: number) => ['sender-profile-id', senderProfileId],
    SENDER_PROFILES: (type?: string) => ['sender-profiles', type],
    SMS_SENDER_PROFILE_ID: (smsSenderProfileId: number | null) => ['sms-sender-profile-id', smsSenderProfileId],
    SENDS: (path: string) => ['sends', path],
    SEND_ID: (sendId: string) => ['send-id', sendId],
    SEND_ID_CLICKABLE_URLS: (sendId: number) => ['send-id-clickable-urls', sendId],
    SEND_IDS_CLICKABLE_URLS: (sendIds: number[]) => ['send-ids-clickable-urls', sendIds],
    SMS_ID: (smsId?: string) => ['sms', smsId],
    SMSES: ['smses'],
    WHATSAPP_TEMPLATE_ID: (whatsAppTemplateId?: string) => ['whatsapp-template', whatsAppTemplateId],
    WHATSAPP_TEMPLATES: ['whatsapp-templates'],
    TEMPLATES: ['templates'],
    TEMPLATE_HTML: (templateId: number) => ['template-html', templateId],
    TEMPLATE_ID: (templateId: string) => ['template-id', templateId],
    TEMPLATE_ID_SUBJECT_LINES: (templateId: number) => ['template-id-subject-lines', templateId],
    VOICE_CONTENT_LIST: ['voice-content-list'],
    VOICE_CONTENT_ID: (voiceContentId: number) => ['voice-content-id', voiceContentId],
    INCONNECT_TEMPLATE: (id: number) => ['inconnect-template', id],
    INCONNECT_TEMPLATES: (offset?: number, name?: string) => ['inconnect-templates', offset, name],
    ROW_DISPLAY_CONDITIONS: ['row-display-conditions'],
    DYNAMIC_CONTENT_CATEGORIES: ['dynamic-content-categories'],
    ARCHIVED_CONTENT: ['archived-content'],
    ARCHIVED_CONTENT_PAGINATED: (
        type?: string,
        searchTerm?: string,
        status?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['archived-content', 'paginated', type, searchTerm, status, offset, limit, sortColumn, sortDirection],
    ARCHIVED_SEGMENTS: ['archived-segments'],
    ARCHIVED_SEGMENTS_PAGINATED: (
        searchTerm?: string,
        offset?: number,
        limit?: number,
        sortColumn?: string,
        sortDirection?: string
    ) => ['archived-segments', 'paginated', searchTerm, offset, limit, sortColumn, sortDirection],
    ACCESS_CENTRE_ME: () => ['access-centre', 'me'],
    ACCESS_CENTRE_PERMISSIONS: (limit?: number, offset?: number) => ['access-centre', 'permissions', limit, offset],
    ACCESS_CENTRE_ROLES: (
        limit?: number,
        offset?: number,
        searchTerm?: string,
        sortColumn?: string,
        sortDirection?: string
    ) => ['access-centre', 'roles', limit, offset, searchTerm, sortColumn, sortDirection],
    ACCESS_CENTRE_ROLE_ID: (roleId: number | string) => ['access-centre', 'roles', roleId],
    ACCESS_CENTRE_USERS: (
        limit?: number,
        offset?: number,
        searchTerm?: string,
        sortColumn?: string,
        sortDirection?: string
    ) => ['access-centre', 'users', limit, offset, searchTerm, sortColumn, sortDirection],
    ACCESS_CENTRE_USER_ID: (userId: string) => ['access-centre', 'users', userId],
    BRAND_PROFILE: ['brand-profile'],
    COPY_GENERATOR_HISTORY: (templateId: number) => ['copy-generator-history', templateId],
    AI_GUARDRAILS: ['ai-guardrails'],
};

export default QueryKeys;
